/*
 * Overlay coexistence: Lumen layers with Mantine's (revamp plan §7.1). Proved by the /prototype/stacking
 * page, removed in PR 10a (in git at 0fe03d3b6:lumen-FE/src/prototype/StackingCheck.tsx).
 * Lumen layers among themselves need nothing here: every Lumen
 * overlay comes from the one Radix copy in node_modules (`radix-ui` and the direct @radix-ui deps pin
 * the same versions), so Radix's own layer stack handles focus, Escape and outside clicks between them.
 * Keep it that way: a second copy of react-dismissable-layer or react-focus-scope breaks that.
 *
 * Rules for authors:
 * - Mantine inside a Lumen Dialog / Sheet / Popover just works: <MantineLayerHost> (inside every
 *   DialogContent, SheetContent and PopoverContent) retargets Mantine's portals into the Lumen
 *   layer. Focus trap, outside-click, body pointer-events and scroll lock of the Lumen layer all see
 *   the Mantine Modal / Select / DatePicker / Popover as inside, and it paints above the Lumen content.
 *   Do not pass `withinPortal={false}` or a portal `target` to Mantine pickers for this.
 * - Lumen inside a Mantine Modal just works: Lumen layers sit at z-500, above Mantine's 200–400, and
 *   shield their focused element from Mantine Modal's window-level Escape handler.
 * - Escape closes only the top layer, in both directions. Toasts (sonner, Mantine) stay clickable.
 * - Known limit: a Mantine Modal opened from inside a Lumen *Popover* is positioned inside the
 *   popover (Radix positions it with a transform). Close the popover first, or use a Lumen Dialog.
 *
 * Mechanisms (delete 1, 2, 3 and the Mantine half of 4 with Mantine in PR 10):
 * 1. Portal host. A nested MantineThemeProvider sets Portal's default `target` to an empty node
 *    at the end of the Lumen content, fixed at the viewport origin, so Mantine layers are DOM
 *    children of the Lumen layer. Popover and Tooltip default to `floatingStrategy: 'fixed'` there,
 *    so a caller's `overflow-y-auto` on the content cannot clip their dropdowns. This is why Lumen
 *    dialogs are centred with `inset-0 m-auto`, not a translate: a transform would make the
 *    dialog the containing block of a Mantine Modal's `position: fixed`.
 * 2. Tab. Radix's focus trap loops over the whole content, Mantine layers included; inside a
 *    Mantine layer in the host, Tab at its edges wraps within that layer instead.
 * 3. Escape. Radix dismisses its top layer from a document listener, Mantine Modal from a window
 *    listener, and Radix does not see Mantine layers. `isEscapeForAnotherLayer` keeps a Lumen layer
 *    open when the key belongs to a Mantine layer in its host or a Mantine element that claims
 *    Escape (`data-mantine-stop-propagation`, set by open Comboboxes). When a Lumen layer (Select
 *    and menu included) opens over a Mantine Modal, its focused element carries
 *    `data-mantine-stop-propagation`, Mantine's own opt-out, so the modal underneath stays open.
 * 4. Toasts. Clicking a sonner or Mantine notification is not an outside click, and both stay
 *    clickable under Radix's `body { pointer-events: none }`.
 */
import { MantineThemeProvider } from '@mantine/core';
import type * as React from 'react';
import { useId, useMemo } from 'react';

const HOST_ATTR = 'data-lumen-mantine-host';
const HOST_SELECTOR = `[${HOST_ATTR}]`;
const SHIELD_ATTR = 'data-lumen-escape-shield';
const MANTINE_STOP = 'data-mantine-stop-propagation';
const TOASTS = '[data-sonner-toaster], .mantine-Notifications-root';
// Mantine Modal and Drawer content.
const MANTINE_MODAL = 'section[role="dialog"][aria-modal="true"]';
// A Radix modal sets `pointer-events: none` on <body>; Mantine notifications (in <body>) inherit it.
// React 19 hoists this into <head> once, however many layers render it.
const NOTIFICATIONS_CSS = '.mantine-Notification-root { pointer-events: auto; }';
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/** Renders children with Mantine's portals retargeted into this Lumen layer. */
function MantineLayerHost({ children }: { children: React.ReactNode }) {
  const id = useId();
  // A selector, not the node: Mantine deep-merges nested themes, and a DOM node does not survive that.
  const theme = useMemo(
    () => ({
      components: {
        Portal: { defaultProps: { target: `[${HOST_ATTR}="${id}"]` } },
        Popover: { defaultProps: { floatingStrategy: 'fixed' } },
        Tooltip: { defaultProps: { floatingStrategy: 'fixed' } },
      },
    }),
    [id]
  );
  return (
    <MantineThemeProvider theme={theme}>
      <style href="lumen-mantine-coexistence" precedence="default">
        {NOTIFICATIONS_CSS}
      </style>
      {children}
      {/* Fixed at the viewport origin: Mantine places its fixed layers (Modal inner) with `left: auto`,
          so their static position must be where it would be in <body>. */}
      <div {...{ [HOST_ATTR]: id }} className="fixed top-0 left-0 size-0" />
    </MantineThemeProvider>
  );
}

function asElement(target: EventTarget | null): Element | null {
  return target instanceof Element ? target : null;
}

/** True when this Escape belongs to a layer above the Lumen layer that received it. */
function isEscapeForAnotherLayer(event: KeyboardEvent): boolean {
  const target = asElement(event.target);
  if (!target) return false;
  if (target.closest(HOST_SELECTOR)) return true;
  return target.getAttribute(MANTINE_STOP) === 'true' && !target.hasAttribute(SHIELD_ATTR);
}

function isToast(target: EventTarget | null): boolean {
  return asElement(target)?.closest(TOASTS) != null;
}

function mantineModalOutside(content: Element): boolean {
  return Array.from(document.querySelectorAll(MANTINE_MODAL)).some((modal) => !content.contains(modal));
}

function shieldFocused(event: React.FocusEvent<HTMLElement>) {
  const target = asElement(event.target);
  if (!target || target.closest(HOST_SELECTOR) || target.hasAttribute(MANTINE_STOP)) return;
  if (!mantineModalOutside(event.currentTarget)) return;
  target.setAttribute(MANTINE_STOP, 'true');
  target.setAttribute(SHIELD_ATTR, '');
}

function unshieldBlurred(event: React.FocusEvent<HTMLElement>) {
  const target = asElement(event.target);
  if (!target?.hasAttribute(SHIELD_ATTR)) return;
  target.removeAttribute(MANTINE_STOP);
  target.removeAttribute(SHIELD_ATTR);
}

function isTabbable(el: HTMLElement) {
  return el.tabIndex >= 0 && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
}

/** Tab at the edge of a Mantine layer in the host wraps within that layer. */
function scopeTabToMantineLayer(event: React.KeyboardEvent<HTMLElement>) {
  if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
  const target = asElement(event.target);
  const host = target?.closest(HOST_SELECTOR);
  if (!target || !host) return;
  const layer = Array.from(host.children).find((child) => child.contains(target));
  if (!layer) return;
  const tabbables = Array.from(layer.querySelectorAll<HTMLElement>(TABBABLE)).filter(isTabbable);
  if (tabbables.length === 0) return;
  const first = tabbables[0];
  const last = tabbables[tabbables.length - 1];
  const next = event.shiftKey ? (target === first ? last : null) : target === last ? first : null;
  if (!next) return;
  event.preventDefault();
  event.stopPropagation();
  next.focus();
}

type LayerHandlers = {
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: CustomEvent<{ originalEvent: PointerEvent }>) => void;
  onFocusCapture?: React.FocusEventHandler<HTMLDivElement>;
  onBlurCapture?: React.FocusEventHandler<HTMLDivElement>;
  onKeyDownCapture?: React.KeyboardEventHandler<HTMLDivElement>;
};

/** The coexistence handlers for a Lumen layer's Content, composed with the caller's own. */
function layerGuards(props: LayerHandlers): Required<LayerHandlers> {
  return {
    onEscapeKeyDown: (event) => {
      props.onEscapeKeyDown?.(event);
      if (isEscapeForAnotherLayer(event)) event.preventDefault();
    },
    onPointerDownOutside: (event) => {
      props.onPointerDownOutside?.(event);
      if (isToast(event.target)) event.preventDefault();
    },
    onFocusCapture: (event) => {
      props.onFocusCapture?.(event);
      shieldFocused(event);
    },
    onBlurCapture: (event) => {
      props.onBlurCapture?.(event);
      unshieldBlurred(event);
    },
    onKeyDownCapture: (event) => {
      props.onKeyDownCapture?.(event);
      scopeTabToMantineLayer(event);
    },
  };
}

/** Handlers for Lumen Select and menu content: the Mantine Escape shield of 3 above. */
function menuLayerGuards(props: Pick<LayerHandlers, 'onFocusCapture' | 'onBlurCapture'>) {
  return {
    onFocusCapture: (event: React.FocusEvent<HTMLDivElement>) => {
      props.onFocusCapture?.(event);
      shieldFocused(event);
    },
    onBlurCapture: (event: React.FocusEvent<HTMLDivElement>) => {
      props.onBlurCapture?.(event);
      unshieldBlurred(event);
    },
  };
}

export { MantineLayerHost, layerGuards, menuLayerGuards };
